import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import axios from "axios";
import { API_BASE_URL } from "../../config/constants";

const ApplyListComponent = ({ applyList, onChange }) => {
  const [salesData, setSalesData] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/apply-coupon-options`);
        setSalesData(res.data);
      } catch (err) {
        console.error("Failed to fetch apply list options", err);
      }
    };
    fetchData();
  }, []);

  const getFilteredData = () => {
    let data = salesData
      .filter((item) => !minPrice || item.price >= parseFloat(minPrice))
      .filter((item) => !maxPrice || item.price <= parseFloat(maxPrice))
      .filter((item) => !stateFilter || item.state === stateFilter)
      .filter((item) => !statusFilter || item.sales_status === statusFilter);

    switch (sortBy) {
      case "price-asc":
        data = data.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        data = data.sort((a, b) => b.price - a.price);
        break;
      case "date-desc":
        data = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      default:
        data = data.sort((a, b) => a.phone_last10.localeCompare(b.phone_last10));
    }

    return data;
  };

  const filteredData = getFilteredData();

  const handleSelectOne = (phone) => {
    if (applyList.includes(phone)) {
      onChange(applyList.filter((item) => item !== phone));
    } else {
      onChange([...applyList, phone]);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    const phones = filteredData.map((item) => item.phone_last10);
    onChange(checked ? Array.from(new Set([...applyList, ...phones])) : []);
  };

  const handleClearFilters = () => {
    setMinPrice("");
    setMaxPrice("");
    setStateFilter("");
    setStatusFilter("");
    setSortBy("");
    setSelectAll(false);
  };

  const uniqueStates = [...new Set(salesData.map((item) => item.state).filter(Boolean))];
  const uniqueStatuses = [...new Set(salesData.map((item) => item.sales_status).filter(Boolean))];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Filter and Select Customers
      </Typography>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          label="Min Price"
          type="number"
          value={minPrice}
          onChange={(e) => setMinPrice(e.target.value)}
        />
        <TextField
          label="Max Price"
          type="number"
          value={maxPrice}
          onChange={(e) => setMaxPrice(e.target.value)}
        />
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>State</InputLabel>
          <Select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} label="State">
            <MenuItem value="">All</MenuItem>
            {uniqueStates.map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            {uniqueStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Sort By</InputLabel>
          <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sort By">
            <MenuItem value="">Phone (default)</MenuItem>
            <MenuItem value="price-asc">Price: Low to High</MenuItem>
            <MenuItem value="price-desc">Price: High to Low</MenuItem>
            <MenuItem value="date-desc">Date: Newest First</MenuItem>
          </Select>
        </FormControl>

        <Button variant="outlined" color="secondary" onClick={handleClearFilters}>
          Clear Filters & Sort
        </Button>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={selectAll}
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        }
        label="Select All in Filtered List"
      />

      <Box mt={2} mb={1}>
        <Typography variant="subtitle1">
          Selected Phone Numbers: {applyList.length}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Select</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Pincode</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={applyList.includes(item.phone_last10)}
                    onChange={() => handleSelectOne(item.phone_last10)}
                  />
                </TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.phone_last10}</TableCell>
                <TableCell>{item.email}</TableCell>
                <TableCell>{item.state}</TableCell>
                <TableCell>{item.pincode}</TableCell>
                <TableCell>{item.price}</TableCell>
                <TableCell>{item.sales_status}</TableCell>
                <TableCell>{item.created_at?.slice(0, 10)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Typography variant="subtitle2">Selected Phone List:</Typography>
        <Typography variant="body2">
          {applyList.length > 0 ? applyList.join(", ") : "None selected"}
        </Typography>
      </Box>
    </Box>
  );
};

export default ApplyListComponent;
